import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import beTimeOffCategories from 'actions/timeoff/beTimeOffCategories.action';
import beTimeOffReport from 'actions/timeoff/beTimeOffReport.action';
import filterActiveStylists from 'bl/data/filterActiveStylists.bl';
import Permitted from 'components/core/Permitted.component';
import Button from 'components/form/Button.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import ToggleSwitchPanel from 'components/form/ToggleSwitchPanel.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentComingSoon from 'components/generic/TooltipContentComingSoon.component';
import FilterReports from 'components/timeoff/FilterReports.component';
import StylistAllowancesDrawer from 'components/timeoff/StylistAllowancesDrawer.component';
import StylistAllowancesExcelTable from 'components/timeoff/StylistAllowancesExcelTable.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonId, selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';

import CN from './ReportsTimeOffPage.module.scss';

const L = logger('ReportsTimeOffPage');
const getHeaders = (t) =>
  Object.freeze([
    t('staffKpisReports.reportsTimeOff.headers.category'),
    t('staffKpisReports.reportsTimeOff.headers.allowance'),
    t('staffKpisReports.reportsTimeOff.headers.used'),
    t('staffKpisReports.reportsTimeOff.headers.future'),
    t('staffKpisReports.reportsTimeOff.headers.remaining'),
    t('staffKpisReports.reportsTimeOff.headers.excess'),
  ]);

const ReportsTimeOffPage = ({
  beTimeOffCategories,
  beTimeOffReport,
  fetchStylistsBySalon,
  isDarkMode,
  stylists,
  salonId,
}) => {
  const { val: isDaySelected, inv: isHoursSelected, yay: switchHoursToDays, nay: switchDaysToHours } = useTip(true);
  const { val: isYearTable, inv: isRangeTable, yay: switchRangeToYear, nay: switchYearToDay } = useTip(true);
  const { t } = useTranslation();

  const HEADERS = getHeaders(t);

  const linkReports = linkTo({ pattern: LINK.reportsHours, params: { salonId }, fault: '#' });

  useEffect(() => {
    // NOTE: preload data that will be used in child components through the Redux store
    L.unignore('useEffect()', 'beTimeOffReport:', beTimeOffReport());
    L.unignore('useEffect()', 'fetchStylistsBySalon:', fetchStylistsBySalon(salonId));
    L.unignore('useEffect()', 'beTimeOffCategories:', beTimeOffCategories({ salonId }));
  }, [fetchStylistsBySalon, salonId, beTimeOffCategories, beTimeOffReport]);

  return (
    <div
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <Permitted value={PERM.canViewKpiReportsPersonal}>
        <br />
        <AlertPanel tag="AlertPanel" variant="warningPanel">
          <div className={CN.alertText}>
            {t('staffKpisReports.reportsTimeOff.missingPermissions')}
            <br />
            {t('staffKpisReports.reportsTimeOff.accessMessage')}
          </div>
        </AlertPanel>
      </Permitted>
      <Permitted value={PERM.canViewKpiReportsAllStaff}>
        <FilterReports tableId="TimeOffExcel">
          <div data-bem="ReportsTimeOffPage_toggle-tables" className={CN.rangeContainer}>
            <div data-bem="ReportsTimeOffPage__toggle-year-range" className={CN.toggleSwitchContainer}>
              <div>
                <ToggleSwitchPanel>
                  <ToggleSwitch
                    selected={isYearTable}
                    onClick={switchRangeToYear}
                    name={t('staffKpisReports.reportsTimeOff.yearToggle')}
                    size="normal"
                  />

                  <Tooltip variant="comingSoon" position="top" content={<TooltipContentComingSoon />}>
                    <ToggleSwitch
                      disabled
                      selected={isRangeTable}
                      onClick={switchYearToDay}
                      name={t('staffKpisReports.reportsTimeOff.dateRangeToggle')}
                      size="normal"
                    />
                  </Tooltip>
                </ToggleSwitchPanel>
              </div>

              <div className={CN.infoContainer}>
                <div className={CN.info}>{t('staffKpisReports.reportsTimeOff.dateRangeComingSoon')}</div>
                <div>
                  <NavLink to={linkReports}>
                    <Button variant="primaryForce" onClick={alwaysNoop}>
                      <span className={CN.buttonSpace}>{t('staffKpisReports.reportsTimeOff.viewReportButton')}</span>
                    </Button>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
          {/* NOTE: container for feature task  - date picker */}

          <div data-bem="ReportsTimeOffPage__picker" className={CN.pickerContainer} />
          <div data-bem="ReportsTimeOffPage__toggle-day-hours" className={CN.toggleSwitchContainer}>
            <ToggleSwitchPanel>
              <ToggleSwitch
                selected={isDaySelected}
                onClick={switchHoursToDays}
                name={t('staffKpisReports.reportsTimeOff.daysToggle')}
                size="normal"
              />
              <ToggleSwitch
                selected={isHoursSelected}
                onClick={switchDaysToHours}
                name={t('staffKpisReports.reportsTimeOff.hoursToggle')}
                size="normal"
              />
            </ToggleSwitchPanel>
          </div>
        </FilterReports>

        {isYearTable &&
          filterActiveStylists(stylists).map((stylist) => (
            <div data-bem="ReportsTimeOffPage__stylist" key={stylist.id} className={CN.tableItem}>
              <StylistAllowancesDrawer stylist={stylist} headers={HEADERS} isHoursSelected={isHoursSelected} />
            </div>
          ))}

        {isRangeTable && <div>{t('staffKpisReports.reportsTimeOff.comingSoon')}</div>}

        {/* NOTE: this is for export all stylist data as excel */}
        {filterActiveStylists(stylists).map((stylist) => (
          <div data-bem="ReportsTimeOffPage__table-excel" data-key={stylist.id} key={stylist.id}>
            <StylistAllowancesExcelTable stylist={stylist} headers={HEADERS} isHoursSelected={isHoursSelected} />
          </div>
        ))}
      </Permitted>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  stylists: selectCurrentSalonStylists(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ beTimeOffCategories, fetchStylistsBySalon, beTimeOffReport }, dispatch);

const ReportsTimeOffPageHoc = connect(mapStateToProps, mapDispatchToProps)(withTheme()(ReportsTimeOffPage));

export default ReportsTimeOffPageHoc;
